import { useEffect, useState } from "react";
import type { LangCode } from "@/lib/i18n";
import { safeDispatchEvent, safeLocalStorageGet, safeLocalStorageSet } from "@/lib/safe-browser";

const KEY = "routify.lang";
const SUPPORTED: LangCode[] = ["en", "fr", "es", "pt", "ja", "de", "it"];

function readStored(): LangCode | null {
  if (typeof window === "undefined") return null;
  const v = safeLocalStorageGet(KEY) as LangCode | null;
  return v && SUPPORTED.includes(v) ? v : null;
}

/**
 * Pure helper: given a list of browser language tags (e.g. from
 * `navigator.languages`), returns the first supported 2-letter language
 * code, or null if none match. Exported for testing.
 */
export function detectBrowserLang(langs: readonly string[]): LangCode | null {
  for (const raw of langs) {
    if (!raw) continue;
    const code = raw.slice(0, 2).toLowerCase() as LangCode;
    if (SUPPORTED.includes(code)) return code;
  }
  return null;
}

export function useLang() {
  const [lang, setLangState] = useState<LangCode>(() => readStored() ?? "pt");



  useEffect(() => {
    const stored = readStored();
    if (stored) {
      setLangState(stored);
    } else if (typeof navigator !== "undefined") {
      // No stored preference yet: detect from the browser, without causing
      // a hydration mismatch (this only runs on the client, post-mount).
      const candidates = navigator.languages?.length ? navigator.languages : [navigator.language];
      const detected = detectBrowserLang(candidates ?? []);
      if (detected) setLangState(detected);
    }

    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY && e.newValue) setLangState(e.newValue as LangCode);
    };
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent<LangCode>).detail;
      if (detail) setLangState(detail);
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("routify:lang", onCustom as EventListener);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("routify:lang", onCustom as EventListener);
    };
  }, []);

  const setLang = (next: LangCode) => {
    setLangState(next);
    if (typeof window !== "undefined") {
      safeLocalStorageSet(KEY, next);
      safeDispatchEvent(new CustomEvent("routify:lang", { detail: next }));
    }
  };

  return { lang, setLang };
}
